import React, { useState } from 'react';
import { trackFormStart, trackFormSubmit, trackSampleReportRequest } from '@/utils/analytics';
import { CheckCircle } from 'lucide-react';
import { Turnstile } from '@/components/ui/Turnstile';

interface GatedAssetFormProps {
  assetName: string;
  buttonText: string;
  eventName: string;
  ctaPosition: string;
  downloadUrl?: string;
  mailchimpTag?: string;
  consentLabel?: string;
  onSuccess?: () => void;
}

export const GatedAssetForm: React.FC<GatedAssetFormProps> = ({ 
  assetName, 
  buttonText, 
  eventName, 
  ctaPosition,
  downloadUrl,
  mailchimpTag,
  consentLabel,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    organization: ''
  });
  
  const [consent, setConsent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleFocus = () => {
    if (!hasStarted) {
      setHasStarted(true);
      trackFormStart(eventName, ctaPosition);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    
    // Inline Validation
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Please tell us your name so we know who to send this to.";
    if (!formData.email.trim()) newErrors.email = "We need your email address to send the asset.";
    else if (!validateEmail(formData.email)) newErrors.email = "Please enter a valid email address.";
    
    if (!formData.role.trim()) newErrors.role = "Please let us know your role.";
    if (!formData.organization.trim()) newErrors.organization = "Please let us know your organisation.";

    if (!turnstileToken) {
      newErrors.turnstile = "Please complete the Turnstile verification.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    // Send subscriber & lead to Mailchimp
    const nameParts = formData.name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    fetch('/api/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: formData.email.trim(),
        firstName,
        lastName,
        role: formData.role,
        organization: formData.organization,
        consent,
        tags: ['Gated Asset', mailchimpTag || `Asset: ${assetName}`, formData.role ? `Role: ${formData.role}` : ''].filter(Boolean),
        turnstileToken,
        company_website: companyWebsite,
        downloadUrl,
        assetName
      })
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Failed to submit form.');
        }
        setIsSubmitting(false);
        setIsSuccess(true);
        if (eventName === 'sample_report_request') {
          trackSampleReportRequest(ctaPosition);
        }
        trackFormSubmit(eventName, ctaPosition);
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        console.warn('Mailchimp gated asset sync notice:', err);
        setIsSubmitting(false);
        setSubmitError("Something went wrong. Please try again, or email marc@enneainternational.com.");
        setTurnstileToken('');
        setResetKey(prev => prev + 1);
      });
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center bg-ground-alt border border-ground-deep">
        <CheckCircle className="w-12 h-12 text-rule-3 mb-4" />
        <h3 className="text-2xl font-serif text-ink mb-2">Your {assetName} is ready.</h3>
        {formData.email && (
          <p className="text-sm text-ink/80 mb-6">We&apos;ve also emailed a confirmation copy to {formData.email}.</p>
        )}
        <div className="my-3">
          <a
            href={downloadUrl || "/downloads/five-lens-sample-report.pdf"}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="inline-flex items-center justify-center px-8 py-4 bg-rule-3 text-white text-xs font-bold uppercase tracking-widest hover:bg-rule-3/90 transition-colors shadow-sm"
          >
            Download the PDF
          </a>
        </div>
        <div className="mt-4 pt-2">
          <a
            href="https://outlook.office.com/book/Schedulea20minuteDiscoverySession@enneainternational.com/?ismsaljsauthenabled"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-bold uppercase tracking-widest text-ink/70 hover:text-rule-3 underline underline-offset-4 transition-colors"
          >
            Want to talk it through? Book a free 20-minute call
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot */}
      <input
        type="text"
        name="company_website"
        value={companyWebsite}
        onChange={(e) => setCompanyWebsite(e.target.value)}
        style={{ display: 'none' }}
        tabIndex={-1}
        autoComplete="off"
      />

      <div>
        <label htmlFor="name" className="block text-sm font-bold text-ink mb-2">Full Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          onFocus={handleFocus}
          className={`w-full px-4 py-3 bg-white border ${errors.name ? 'border-rule-3' : 'border-ground-deep'} focus:border-rule-3 focus:ring-1 focus:ring-rule-3 outline-none transition-all text-ink`}
          placeholder="Alex Smith"
        />
        {errors.name && <p className="mt-2 text-sm text-rule-3 font-medium">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-bold text-ink mb-2">Work Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onFocus={handleFocus}
          className={`w-full px-4 py-3 bg-white border ${errors.email ? 'border-rule-3' : 'border-ground-deep'} focus:border-rule-3 focus:ring-1 focus:ring-rule-3 outline-none transition-all text-ink`}
          placeholder="jane@company.com"
        />
        {errors.email && <p className="mt-2 text-sm text-rule-3 font-medium">{errors.email}</p>}
        
        <div className="flex items-start gap-3 mt-3">
          <input
            type="checkbox"
            id="consent"
            name="consent"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-ground-deep text-rule-3 focus:ring-rule-3 cursor-pointer shrink-0"
          />
          <label htmlFor="consent" className="text-xs text-ink/80 leading-relaxed cursor-pointer select-none">
            {consentLabel ? consentLabel : (assetName === 'Curriculum PDF'
              ? 'Email me the curriculum, plus occasional FIVE LENS insights and event invitations.'
              : 'Email me the report, plus occasional FIVE LENS insights and event invitations.')}
          </label>
        </div>
      </div>

      <div>
        <label htmlFor="role" className="block text-sm font-bold text-ink mb-2">Your Role</label>
        <input
          type="text"
          id="role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          onFocus={handleFocus}
          className={`w-full px-4 py-3 bg-white border ${errors.role ? 'border-rule-3' : 'border-ground-deep'} focus:border-rule-3 focus:ring-1 focus:ring-rule-3 outline-none transition-all text-ink`}
          placeholder="e.g. Head of L&D, Executive Coach"
        />
        {errors.role && <p className="mt-2 text-sm text-rule-3 font-medium">{errors.role}</p>}
      </div>

      <div>
        <label htmlFor="organization" className="block text-sm font-bold text-ink mb-2">Organisation</label>
        <input
          type="text"
          id="organization"
          name="organization"
          value={formData.organization}
          onChange={handleChange}
          onFocus={handleFocus}
          className={`w-full px-4 py-3 bg-white border ${errors.organization ? 'border-rule-3' : 'border-ground-deep'} focus:border-rule-3 focus:ring-1 focus:ring-rule-3 outline-none transition-all text-ink`}
          placeholder="Company Name"
        />
        {errors.organization && <p className="mt-2 text-sm text-rule-3 font-medium">{errors.organization}</p>}
      </div>

      <Turnstile 
        onVerify={(token) => { setTurnstileToken(token); setErrors(prev => { const n = {...prev}; delete n.turnstile; return n; }); }}
        onExpire={() => { setTurnstileToken(''); setErrors(prev => ({ ...prev, turnstile: 'Verification failed, please try again' })); setResetKey(prev => prev + 1); }}
        onError={() => { setTurnstileToken(''); setErrors(prev => ({ ...prev, turnstile: 'Verification failed, please try again' })); setResetKey(prev => prev + 1); }}
        resetKey={resetKey} 
      />
      {errors.turnstile && <p className="text-sm text-rule-3 font-medium text-center">{errors.turnstile}</p>}

      {submitError && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 text-sm leading-relaxed text-center font-medium">
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting || !turnstileToken}
        className="w-full bg-ink text-white font-bold tracking-widest uppercase text-sm py-4 px-6 hover:bg-rule-3 transition-colors mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Sending...' : (!turnstileToken ? 'Verifying…' : buttonText)}
      </button>
    </form>
  );
};
